import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faSignOutAlt,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faUser,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '../config/api.config';
import Footer from '../components/Footer';
import { getProfile } from '../services/api';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import Sidebar from '../components/Sidebar/Sidebar';
import logoMain from '../assets/images/logo-main.png';

// Styled Components
const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => props.isCollapsed ? '70px' : '240px'};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 76px;
  transition: margin-left 0.3s ease;
  width: calc(100% - ${props => props.isCollapsed ? '70px' : '240px'});

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 76px;
    transition: none;
  }
`;

const TopBar = styled.div<{ isCollapsed?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => props.isCollapsed ? '70px' : '240px'};
  z-index: 999;
  transition: left 0.3s ease;

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    transition: none;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 768px) {
    justify-content: flex-end;
    padding: 0;
    gap: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff6b6b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  margin-left: 8px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + -7px);
  right: 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 180px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }

  svg {
    width: 16px;
    color: #ff6b6b;
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 90%;
  max-width: 1400px;
  margin: 40px auto;

  @media (max-width: 768px) {
    width: calc(100% - 40px);
    margin: 20px auto;
  }

  @media (max-width: 480px) {
    width: calc(100% - 20px);
    margin: 15px auto;
  }
`;

const ProfileHeader = styled.div`
  background: #ff6b6b;
  color: white;
  padding: 16px 24px;
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    font-size: 20px;
  }
`;

const ProfileTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 500;
`;

const ProfileSection = styled.div`
  padding: 24px;
  width: 100%;
`;

const ProfileField = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const FieldLabel = styled.div`
  font-size: 15px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
`;

const FieldValue = styled.div`
  font-size: 16px;
  color: #333;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  width: 100%;
  overflow-wrap: break-word;
`;

const PageContainer = styled.div`
  padding: 0 20px;
  width: 100%;
`;

const Profile: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getProfile();
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const handleLogout = () => {
    handleLogoutClick();
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{profile?.name || ''}</UserName>
            </UserInfo>
            <UserAvatar>{profile?.name?.charAt(0) || ''}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <PageContainer>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <ProfileCard>
              <ProfileHeader>
                <FontAwesomeIcon icon={faUser} />
                <ProfileTitle>Profile Information</ProfileTitle>
              </ProfileHeader>

              <ProfileSection>
                <ProfileField>
                  <FieldLabel>User Name</FieldLabel>
                  <FieldValue>{profile?.name || 'N/A'}</FieldValue>
                </ProfileField>

                <ProfileField>
                  <FieldLabel>Email</FieldLabel>
                  <FieldValue>{profile?.email || 'N/A'}</FieldValue>
                </ProfileField>

                <ProfileField>
                  <FieldLabel>Phone</FieldLabel>
                  <FieldValue>{profile?.phone || 'N/A'}</FieldValue>
                </ProfileField>

                <ProfileField>
                  <FieldLabel>Member Since</FieldLabel>
                  <FieldValue>
                    {profile?.createdAt
                      ? new Date(profile.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </FieldValue>
                </ProfileField>
              </ProfileSection>
            </ProfileCard>
          )}
        </PageContainer>
        <Footer isCollapsed={isCollapsed} />
      </MainContent>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default Profile; 